import cn from 'classnames';
import { type ReactNode, useCallback, useMemo, useRef } from 'react';

import { type BaseRowData } from '@adv-frontend/data-provider';
import { getScrollbarClassName, useObjectRef } from '@direct-frontend/components';
import {
    debounce,
    genericForwardRef,
    genericMemo,
    NormalizeUnion,
    throttle,
    useIsomorphicLayoutEffect,
} from '@direct-frontend/stdlib';

import type { GridColumnOrder } from '../../adapters/columnsOrderAdapter';
import { useGrid, type GridSortingOptions } from '../../hooks/useGrid';
import type { GridColumnSizing } from '../../hooks/useGridColumnSizing';
import type { GridColumnPinning } from '../../hooks/useGridColumnPinning';
import type { GridColumnSorting } from '../../hooks/useGridColumnSorting';
import { useGridScrollDirection } from '../../hooks/useGridScrollDirection';
import type { GridColumnVisibility } from '../../adapters/columnsVisibilityAdapter';
import type { GridRowsExpanded } from '../../hooks/useGridRowExpanded';
import type { GridRowsCount } from '../../hooks/useGridRowsCount';
import type { TableMeta } from '../../types/tanstack-table-types';
import type {
    GridColumnDefinition,
    GridRef,
    GridOnScrollParams,
    GridVisualOptions,
    GridWrapperComponentType,
    GridWrapperComponentTypeProps,
    GridHeader,
    LoadMoreRowsRowProps,
} from '../../types';

import { Header } from './Header/Header';
import { VirtualBody, type VirtualBodyCommonProps } from './VirtualBody/VirtualBody';
import { WindowKeepMountedGridEffects } from './WindowKeepMountedGridEffects';
import { getRowsTotalCount } from '../../lib/getRowsTotalCount';

import styles from './Grid.module.css';

/**
 * Пропсы для настройки дозагрузки строк грида.
 * Объединяет варианты дозагрузки по клику и по скроллу.
 *
 * @typeParam RowData Данные строки грида.
 */
export type LoadMoreProps<RowData extends BaseRowData> = NormalizeUnion<
    LoadMoreOnClickProps<RowData> | LoadMoreOnScrollProps
>;

/**
 * Пропсы для дозагрузки строк по клику на кнопку "загрузить ещё".
 *
 * @typeParam RowData Данные строки грида.
 */
export type LoadMoreOnClickProps<RowData extends BaseRowData> = {
    /**
     * Способ дозагрузки строк (по скроллу до последней строки или по кнопке "загрузить еще")
     * @default 'onScroll'
     */
    loadMoreRowsType: 'onClick';
    /**
     * Количество строк, добавляемых при нажатии на кнопку "загрузить еще"
     */
    rowsChunkSize: number;
    /**
     * Состояние количества отображаемых строк грида.
     */
    rowsCountState: GridRowsCount;
    /**
     * Метод отрисовки строки дозагрузки строк
     * @param props параметры строки дозагрузки
     * @returns Строка для дозагрузки строк
     */
    renderLoadMoreRow: (props: LoadMoreRowsRowProps<RowData>) => ReactNode;
};

/**
 * Пропсы для дозагрузки строк по скроллу до конца грида.
 */
export type LoadMoreOnScrollProps = {
    /**
     * Способ дозагрузки строк (по скроллу до последней строки или по кнопке "загрузить еще")
     * @default 'onScroll'
     */
    loadMoreRowsType?: 'onScroll';
};

/**
 * Пропсы компонента `Grid`.
 *
 * @typeParam RowData Данные таблицы.
 */
export type LegacyGridProps<RowData extends BaseRowData> = VirtualBodyCommonProps<RowData> & {
    /**
     * CSS-класс, который будет добавлен к контейнеру грида.
     */
    className?: string;
    /**
     * Массив, описывающий колонки грида.
     */
    columns: GridColumnDefinition<RowData, string>[];
    /**
     * Настройка ширины колонок.
     */
    columnSizing?: GridColumnSizing;
    /**
     * Настройка видимости колонок.
     */
    columnVisibility?: GridColumnVisibility;
    /**
     * Настройка порядка колонок.
     */
    columnOrder?: GridColumnOrder;
    /**
     * Настройка фиксирования колонок слева и справа.
     */
    columnPinning?: GridColumnPinning;
    /**
     * Настройка сортировки колонок.
     */
    columnSorting?: GridColumnSorting;
    /**
     * Дефолтные параметры для всех столбцов. Переопределяются параметрами из опции columns.
     */
    defaultColumn?: Partial<GridColumnDefinition<RowData, string>>;
    /**
     * Контекст для ячеек таблицы.
     */
    meta?: TableMeta<RowData>;
    /**
     * Параметры развернутых строк грида.
     */
    rowsExpanded?: GridRowsExpanded;
    /**
     * Параметры отображения грида.
     */
    visualOptions?: GridVisualOptions;
    /**
     * Коллбек скролла грида.
     */
    onScroll?: (params: GridOnScrollParams) => void;
    /**
     * Параметры сортировки грида.
     */
    sortingOptions?: GridSortingOptions;
    /**
     * Обертка для тела грида.
     */
    virtualBodyWrapper?: GridWrapperComponentType;
    /**
     * Дополнительный элемент в конце грида.
     * Используется, чтобы отображать дополнительный контент в конце грида.
     */
    addonBottom?: ReactNode;
    /**
     * Коллбек клика по строке.
     */
    onRowClick?: (rowData: RowData) => void;
} & LoadMoreProps<RowData>;

/**
 * Добавление классов для фиксированных групп столбцов.
 */
function togglePinnedColumnsClassName(grid: HTMLDivElement | null) {
    if (!grid) {
        return;
    }

    // Add horizontalScrolledLeft className
    const horizontalScrolledLeft = styles.horizontalScrolledLeft;
    if (horizontalScrolledLeft) {
        if (grid?.scrollLeft) {
            grid.classList.add(horizontalScrolledLeft);
        } else {
            grid.classList.remove(horizontalScrolledLeft);
        }
    }

    // Add horizontalScrolledRight className
    const horizontalScrolledRight = styles.horizontalScrolledRight;
    if (horizontalScrolledRight) {
        if (grid?.scrollLeft + grid?.clientWidth !== grid?.scrollWidth) {
            grid.classList.add(horizontalScrolledRight);
        } else {
            grid.classList.remove(horizontalScrolledRight);
        }
    }
}

const scrollbarClassName = getScrollbarClassName();

const debounceAndThrottleWaitMs = 500;

/**
 * Компонент грида, адаптированный для работы с useDataProvider и useGrid.
 *
 * Инструкция по определению в макете MiniGrid, Table, Grid
 * https://components.adv.yandex-team.ru/?path=/docs/docs-как-отличить-minigrid-table-grid--docs
 *
 * @param options Опции грида.
 * @typeParam RowData Данные грида.
 * @deprecated Используйте flow-based {@link Grid} (принимает flow-based DataProvider).
 **/
export const LegacyGrid = genericMemo(
    genericForwardRef(function LegacyGrid<RowData extends BaseRowData>(
        props: LegacyGridProps<RowData>,
        forwardedRef: GridRef
    ) {
        const {
            className,
            rowClassName,
            cellSize,
            columnPinning,
            columns,
            columnSizing,
            columnSorting,
            columnVisibility,
            columnOrder,
            dataProvider,
            defaultColumn,
            meta,
            onScroll,
            rowsExpanded,
            selectionManager,
            scrollableContainerClassName,
            sortingOptions,
            virtualization,
            visualOptions,
            virtualBodyWrapper,
            addonBottom,
            onRowClick,
        } = props;
        const isWindowKeepMounted = virtualization.verticalVirtualizationMode === 'window-keep-mounted';
        const ref = useObjectRef(isWindowKeepMounted ? undefined : forwardedRef);
        const gridRef = useRef<HTMLDivElement>(null);
        const gridHeaderRef = useRef<HTMLDivElement>(null);
        const commonResizerRef = useRef<HTMLDivElement>(null);

        const totalCount = useMemo(() => dataProvider.getRows().length, [dataProvider]);
        // Общее кол-во подстрок на гриде (для контроля при дозагрузке по кнопке)
        const subRowsTotalCount = useMemo(() => {
            const rows = dataProvider.getRows();
            const totalCount: Record<string, number> = {};
            getRowsTotalCount(rows, totalCount);
            return totalCount;
        }, [dataProvider]);

        const grid = useGrid(dataProvider, {
            columnPinning,
            columns,
            columnSizing,
            columnSorting,
            columnVisibility,
            columnOrder,
            defaultColumn,
            meta,
            rowsExpanded,
            sortingOptions,
            ...(props.loadMoreRowsType === 'onClick'
                ? ({
                      loadMoreRowsType: 'onClick',
                      rowsChunkSize: props.rowsChunkSize,
                      rowsCount: props.rowsCountState.rowsCount,
                      subRowsCount: props.rowsCountState.subRowsCount,
                  } as const)
                : ({
                      loadMoreRowsType: 'onScroll',
                  } as const)),
        });
        const { rows } = grid.getRowModel();
        const {
            borderRadius,
            rowHighlight,
            highlight,
            hideRowBorderBottom,
            hideHeaderBorderBottom,
            transparentBackground,
        } = visualOptions ?? {};

        const inlineStyles = useMemo(() => {
            if (!borderRadius) {
                return;
            }

            return { borderRadius: Number(borderRadius) };
        }, [borderRadius]);

        useIsomorphicLayoutEffect(() => {
            togglePinnedColumnsClassName(gridRef.current);
        }, [gridRef]);

        const debouncedOnScrollToEnd = useMemo(
            () =>
                debounce(() => {
                    if (typeof ref !== 'function' && ref?.current) {
                        const params: GridOnScrollParams = {
                            startIndex: ref.current.getFirstVisibleRowIndex(),
                            scrollLeft: gridRef.current?.scrollLeft ?? undefined,
                        };

                        if (ref.current.isScrollableByY()) {
                            params.isScrolledToEndByY = ref.current.isScrolledToEndByY();
                        }

                        onScroll?.(params);
                    }
                }, debounceAndThrottleWaitMs),
            [onScroll, ref]
        );

        const throttledTogglePinnedColumnsClassName = useMemo(
            () => throttle(() => togglePinnedColumnsClassName(gridRef.current), debounceAndThrottleWaitMs),
            []
        );

        const [{ verticalScrollDirection }, updateOffset] = useGridScrollDirection();

        const onGridScroll = useCallback(() => {
            if (gridRef.current && !isWindowKeepMounted) {
                updateOffset(gridRef.current.scrollTop);
            }

            throttledTogglePinnedColumnsClassName();
            debouncedOnScrollToEnd();
        }, [debouncedOnScrollToEnd, isWindowKeepMounted, throttledTogglePinnedColumnsClassName, updateOffset]);

        const defaultVirtualBodyWrapper = useCallback(({ children }: GridWrapperComponentTypeProps) => children, []);

        const VirtualBodyWrapper = virtualBodyWrapper || defaultVirtualBodyWrapper;

        // Коллбек для загрузки дополнительных строк
        const handleLoadMore = useCallback(
            (parentRowId?: string) => {
                if (props.loadMoreRowsType !== 'onClick') {
                    return;
                }

                if (parentRowId) {
                    props.rowsCountState.setSubRowsCount(prev => ({
                        ...prev,
                        [parentRowId]: (prev[parentRowId] ?? props.rowsChunkSize) + props.rowsChunkSize,
                    }));
                } else {
                    props.rowsCountState.setRowsCount(prev => prev + props.rowsChunkSize);
                }
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps -- хочет следить за всем rowsCountState, но тот меняется при вызове этого метода
            [
                props.loadMoreRowsType,
                props.rowsCountState?.setSubRowsCount,
                props.rowsCountState?.setRowsCount,
                props.rowsChunkSize,
            ]
        );

        const hasHeader = useMemo(() => {
            let headers: GridHeader<RowData>[] = grid.getCenterHeaderGroups().flatMap(group => group.headers);

            if (grid.getIsSomeColumnsPinned('left')) {
                headers = headers.concat(grid.getLeftHeaderGroups().flatMap(group => group.headers));
            }
            if (grid.getIsSomeColumnsPinned('right')) {
                headers = headers.concat(grid.getRightHeaderGroups().flatMap(group => group.headers));
            }

            return headers.some(header => header.column.columnDef.renderHeader !== undefined);
        }, [grid]);

        return (
            <>
                <div className={styles.commonResizer} ref={commonResizerRef} />
                <VirtualBodyWrapper innerRef={gridRef}>
                    <div
                        data-testid="Modules.Grid"
                        ref={gridRef}
                        className={cn(
                            styles.grid,
                            transparentBackground && styles.transparentBackground,
                            scrollbarClassName,
                            highlight === 'active' && styles.activeGrid,
                            highlight === 'inactive' && styles.inactiveGrid,
                            className
                        )}
                        style={inlineStyles}
                        onScroll={onGridScroll}
                    >
                        {hasHeader && (
                            <Header
                                ref={gridHeaderRef}
                                grid={grid}
                                gridRef={gridRef}
                                commonResizerRef={commonResizerRef}
                                dataProvider={dataProvider}
                                selectionManager={selectionManager}
                                hideHeaderBorderBottom={Boolean(hideHeaderBorderBottom)}
                            />
                        )}

                        <VirtualBody<RowData>
                            cellSize={cellSize}
                            dataProvider={dataProvider}
                            grid={grid}
                            gridForwardRef={ref}
                            gridHeaderRef={gridHeaderRef}
                            gridRef={gridRef}
                            rows={rows}
                            rowClassName={rowClassName}
                            rowHighlight={rowHighlight}
                            scrollableContainerClassName={scrollableContainerClassName}
                            selectionManager={selectionManager}
                            verticalScrollDirection={verticalScrollDirection}
                            virtualization={virtualization}
                            hideRowBorderBottom={Boolean(hideRowBorderBottom)}
                            onRowClick={onRowClick}
                            {...(props.loadMoreRowsType === 'onClick'
                                ? {
                                      loadMoreRowsType: 'onClick',
                                      onLoadMore: handleLoadMore,
                                      totalCount: totalCount,
                                      loadedCount: props.rowsCountState.rowsCount,
                                      subRowsTotalCount: subRowsTotalCount,
                                      subRowsLoadedCount: props.rowsCountState.subRowsCount,
                                      chunkSize: props.rowsChunkSize,
                                      renderLoadMoreRow: props.renderLoadMoreRow,
                                  }
                                : { loadMoreRowsType: 'onScroll' })}
                        />
                        {addonBottom}
                    </div>
                </VirtualBodyWrapper>
                {isWindowKeepMounted && (
                    <WindowKeepMountedGridEffects
                        gridRef={gridRef}
                        innerRef={ref}
                        forwardedRef={forwardedRef}
                        updateOffset={updateOffset}
                        onScroll={debouncedOnScrollToEnd}
                    />
                )}
            </>
        );
    })
);
